import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import { canAccessStaffChat } from 'tripPlanner/utils/staffChatAccess'
import * as storage from 'lib/storage/isomorphicLocalStorage'

const STORAGE_KEY = 'trip-chat-new-hotel-search'
interface NewHotelSearchContextValue {
  canUseNewHotelSearch: boolean
  useNewHotelSearch: boolean
  toggleNewHotelSearch: () => void
}

const NewHotelSearchContext = createContext<NewHotelSearchContextValue | undefined>(undefined)

interface Props {
  children: React.ReactNode
}

export function NewHotelSearchProvider({ children }: Props) {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const email = useAppSelector(state => state.auth.account.email)
  const canUseNewHotelSearch = isLoggedIn && isStaff && canAccessStaffChat(email)
  const [enabled, setEnabled] = useState(false)

  useEffect(() => {
    try {
      setEnabled(storage.get(STORAGE_KEY) === true)
    } catch {
      // Keep the in-memory preference when browser storage is unavailable.
    }
  }, [])

  const toggleNewHotelSearch = useCallback(() => {
    if (!canUseNewHotelSearch) return
    const next = !enabled
    setEnabled(next)
    try {
      storage.set(STORAGE_KEY, next)
    } catch {
      // The current page can still use the preference if storage is full.
    }
  }, [canUseNewHotelSearch, enabled])

  const value = useMemo(() => ({
    canUseNewHotelSearch,
    useNewHotelSearch: canUseNewHotelSearch && enabled,
    toggleNewHotelSearch,
  }), [canUseNewHotelSearch, enabled, toggleNewHotelSearch])

  return <NewHotelSearchContext.Provider value={value}>{children}</NewHotelSearchContext.Provider>
}

export function useNewHotelSearch(): NewHotelSearchContextValue {
  const context = useContext(NewHotelSearchContext)
  if (!context) throw new Error('useNewHotelSearch must be used within NewHotelSearchProvider')
  return context
}
